// @ts-nocheck
import axios from "axios";
import { useState } from "react";
import { Form, Modal, Button, Row, Col, Alert } from "react-bootstrap";

import { useGetAllProductsQuery } from "api/memberPortal/project/product/product.api";
import ReactSelect from "components/ReactSelect";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate } from "utils/timeUtils";

import FileSection from "./FileSection";

const NISTForm = ({ show, onHide, refetch, setAlert, currentProductId }) => {
  const { csrfToken, authToken } = useAuth();
  const [validated, setValidated] = useState(false);
  const [formInfo, setFormInfo] = useState({});
  const [nistFiles, setNistFiles] = useState([]);
  const [formAlert, setFormAlert] = useState({ message: "", type: "" });
  const [errors, setErrors] = useState({});
  const [productValid, setProductValid] = useState(true);
  const [nistEditedFileLabel, setNistEditedFileLabel] = useState("");

  const { data: products = [] } = useGetAllProductsQuery(
    {
      in_eval: true,
      without_nist: true,
    },
    { skip: !show },
  );

  const productOptions = products.map((item) => ({
    value: item.product_id,
    label: "VID" + item.v_id,
  }));

  const validateForm = () => {
    const newErrors = {};
    if (!formInfo.pid && !currentProductId) {
      newErrors.pid = "Associated product is required.";
      setProductValid(false);
    }
    if (formInfo.reqd !== false && nistFiles.length === 0) {
      newErrors.nistFiles = "At least one NIST file is required.";
    }
    if (formInfo.reqd === false && !formInfo.just) {
      newErrors.just = "Justification is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "nist_file") {
      if (files.length) {
        const newFile = {
          file_type: "nist",
          file_source_type: "Certificate Review",
          file_display_name: formInfo.nist_file_display_name,
          uploaded_on: getISOCurrentDate(),
          file_name: files[0].name,
          file_label: formInfo.nist_file_label,
          active: true,
          file: files[0],
          private: formInfo.nist_file_public_status === "Public" ? false : true,
        };
        setNistFiles((prev) => [...prev, newFile]);
      }
    } else if (e.target.name === "nist_file_display_name") {
      setNistEditedFileLabel(e.target.value);
      setFormInfo({
        ...formInfo,
        [e.target.name]: e.target.value,
        nist_file_label: e.target.value,
      });
    } else if (e.target.name === "nist_file_label") {
      setNistEditedFileLabel(e.target.value);
      setFormInfo({ ...formInfo, [e.target.name]: e.target.value });
    } else {
      setFormInfo((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!validateForm()) return;
    setProductValid(true);

    const nistData = new FormData();
    nistData.append("pid", currentProductId ?? formInfo.pid);

    // forInfo.reqd can be undefined if the user doesn't select radio button due to default checked, so I explicitly check for false
    if (formInfo.reqd === false) {
      nistData.append("stid", "No Certificate Review Needed");
      nistData.append("just", formInfo.just);
      nistData.append("reqd", formInfo.reqd);
    } else {
      nistData.append("reqd", true);
    }

    nistFiles.forEach((file) => {
      nistData.append("attachments_file", file.file);
      nistData.append("attachments_source_type", file.file_source_type);
      nistData.append("attachments_type", file.file_display_name);
      nistData.append("attachments_uploaded", file.uploaded_on);
      nistData.append("private", file.private);
      nistData.append("file_label", file.file_label);
    });

    await axios
      .post(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}nist/create_nist_or_update_existing/?pid=${currentProductId ?? formInfo.pid}`,
        nistData,
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        },
      )
      .then(() => {
        setAlert &&
          setAlert({
            message: "Certificate Review added successfully!",
            type: "success",
          });
        onHide();
        refetch();
      })
      .catch((error) => {
        console.log("Unable to submit create or update NIST: ", error);
        setFormAlert({
          message:
            "Error submitting NIST Certificate Review. Please try again.",
          type: "danger",
        });
      });
    setValidated(true);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
        id="nist-creation-form"
      >
        <Modal.Header closeButton>
          <Modal.Title>{`Add Certificate Review ${currentProductId ? `For VID ${currentProductId}` : ""}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!currentProductId && (
            <Row className="mb-3">
              <Col>
                <Form.Label>Associated Product*</Form.Label>
                <ReactSelect
                  id="nist-associated-product-select"
                  options={productOptions}
                  isValid={productValid}
                  name="pid"
                  newObj={formInfo}
                  handleChange={handleChange}
                />
                {errors.pid && (
                  <Form.Text className="text-danger">{errors.pid}</Form.Text>
                )}
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <Form.Check
                type="radio"
                name="reqd"
                label="Required, Please Select Files Below"
                onChange={() => setFormInfo({ ...formInfo, reqd: true })}
                defaultChecked
                data-cy="nist-required-radio"
              />
            </Col>
            <Col>
              <Form.Check
                type="radio"
                name="reqd"
                label="No NIST Review Needed, Please Provide Justification"
                onChange={() => setFormInfo({ ...formInfo, reqd: false })}
                data-cy="nist-not-required-radio"
              />
            </Col>
          </Row>
          {formInfo.reqd === false ? (
            <>
              <Form.Group controlId="justification">
                <Form.Label>Justification*</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="just"
                  value={formInfo.just || ""}
                  onChange={handleChange}
                  isInvalid={!!errors.just}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.just}
                </Form.Control.Feedback>
              </Form.Group>
            </>
          ) : (
            <>
              <FileSection
                formInfo={formInfo}
                setNistFiles={setNistFiles}
                nistFiles={nistFiles}
                handleChange={handleChange}
                nistEditedFileLabel={nistEditedFileLabel}
              />
              {errors.nistFiles && (
                <Alert variant="danger">{errors.nistFiles}</Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {formAlert.message && (
            <Alert variant={formAlert.type}>{formAlert.message}</Alert>
          )}
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Add
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NISTForm;
